import { useRef } from 'react';
import { CloseIcon } from 'components/icons/CloseIcon';
import { SearchIcon } from 'components/icons/SearchIcon';
import { useEffect, useState } from 'react';
import api from 'services/api';
import NoImg from 'assets/img/no-img.jpg';

import { updateTimelineSpeaker, removeTimelineSpeaker } from 'api/timelineAPI';

interface AddSpeakerProps {
  onCancel: () => void;
  speaker?: string;
  people?: any[];
  sentence?: any;
  video_info_hash_id: string;
  videoshot_hash_id: string;
  sentenceHashId: string;
  inTimeline?: boolean;
}

export default function AddSpeaker({
  onCancel,
  sentence,
  speaker,
  people,
  sentenceHashId,
  video_info_hash_id,
  videoshot_hash_id,
  inTimeline
}: AddSpeakerProps) {
  const [peopleToBeAdded, setPeopleToBeAdded] = useState<any[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [searchVal, setSearchVal] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const defaultSpeaker = useRef<any>(null);
  const isMounted = useRef(true);

  const handleCancel = () => {
    onCancel();
  };

  useEffect(() => {
    // Set mounted to true when component mounts
    isMounted.current = true;
    return () => {
      // Set mounted to false when component unmounts
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (speaker) {
      setSelectedPerson(speaker);
      defaultSpeaker.current = speaker;
    }
  }, []);

  useEffect(() => {
    if (people) {
      setPeopleToBeAdded(people);
    }
  }, [people]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  const handleCheckboxChange = (userHashId: string) => {
    setSelectedPerson(userHashId);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple submissions
    const getSpeaker = people && people.find((user) => user.hash_id === selectedPerson);
    setIsSubmitting(true);
    if (sentence && !inTimeline) {
      api
        .put(
          `api/videos/${video_info_hash_id}/shots/${videoshot_hash_id}/summaries/sentences/${sentence?.sentence_hash_id}/`,
          {
            hash_id: selectedPerson,
            object_type: getSpeaker.obj_type
          }
        )
        .then(() => {
          if (isMounted.current) {
            setIsSubmitting(false);
            handleCancel();
          }
        });
    } else if (inTimeline) {
      try {
        await updateTimelineSpeaker(video_info_hash_id, videoshot_hash_id, sentence.hash_id, {
          user_hash_id: selectedPerson,
          object_type: getSpeaker.obj_type
        });
      } catch (error) {
        console.log('error', error);
      } finally {
        // Always reset submitting state and call handleCancel regardless of success/failure
        setIsSubmitting(false);
        handleCancel();
      }
    }
  };

  const handleRemoveSpeaker = async () => {
    if (isSubmitting) return; // Prevent multiple submissions
    setIsSubmitting(true);

    if (inTimeline) {
      try {
        await removeTimelineSpeaker(video_info_hash_id, videoshot_hash_id, sentence.hash_id);
      } catch (err) {
        console.log('error', err);
      } finally {
        setSelectedPerson(null);
        setIsSubmitting(false);
        handleCancel();
      }
    } else {
      api
        .delete(
          `api/videos/${video_info_hash_id}/shots/${videoshot_hash_id}/summaries/sentences/${sentence.sentence_hash_id}/speaker/`
        )
        .then(() => {
          if (isMounted.current) {
            setSelectedPerson(null);
            setIsSubmitting(false);
            handleCancel();
          }
        });
    }
  };

  return (
    <div className="transparent font-lato">
      <div className="relative border bg-gray-bg-dark border-gray-300 rounded-lg bg-white pb-7 pt-9 w-screen md:w-[622px] md:min-h-[200px]">
        <div className="flex px-7">
          <span className="font-bold text-2xl leading-6 text-[#1A1C1D]">Assign Speaker</span>
          <CloseIcon
            className="ml-auto w-5 h-5 cursor-pointer opacity-70 hover:opacity-100"
            onClick={() => onCancel()}
          />
        </div>
        <div className="mt-5">
          <div className="relative mt-4 px-5">
            <SearchIcon className="absolute left-7 w-[15px] h-[15px] top-3" fill="currentColor" />
            <input
              type="text"
              className="pl-8 py-2 w-full mb-4 text-sm text-[#495057] border focus:ring-0 border-[#ced4da] rounded-md leading-6"
              placeholder="Find Members"
              value={searchVal}
              onChange={handleChange}
            />
          </div>
          <div className="h-80 overflow-auto relative">
            {peopleToBeAdded &&
              peopleToBeAdded
                .filter(
                  (item: any) =>
                    (item.name && item.name.toLowerCase().includes(searchVal.toLowerCase())) ||
                    (item.name && item.name.toLowerCase().includes(searchVal.toLowerCase()))
                )
                .sort((a: any, b: any) => a.name?.localeCompare(b.name))
                .map((user: any) => (
                  <div
                    className="flex items-center px-5 py-3 hover:bg-opacity-5 bg-black bg-opacity-0 cursor-pointer relative z-10"
                    key={user.hash_id}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.tagName !== 'INPUT') {
                        handleCheckboxChange(user.hash_id);
                      }
                    }}
                  >
                    <input
                      type="radio"
                      checked={selectedPerson === user.hash_id}
                      onChange={() => {
                        handleCheckboxChange(user.hash_id);
                      }}
                      className="mr-3"
                    />
                    <img
                      src={user.avatar || NoImg}
                      className="w-8 h-8 flex items-center justify-center rounded-full text-white cursor-pointer"
                    />
                    <span className="text-black px-3 py-1 text-base font-bold">{user.name}</span>
                    {defaultSpeaker.current === user.hash_id && (
                      <button
                        onClick={() => handleRemoveSpeaker()}
                        className="text-sm absolute right-5 bg-red-500 text-white font-semibold px-3 py-1 rounded-md hover:bg-red-600 z-40"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
          </div>
        </div>
        <div className="px-5">
          <div className="w-40 h-11 rounded-[10px] text-base font-bold flex items-center justify-center ml-auto mt-5">
            <button
              onClick={handleSubmit}
              disabled={defaultSpeaker.current === selectedPerson} // Disable if selectedPerson is null
              className={`bg-[#0979b7] w-40 h-11 rounded-[10px] font-bold px-3 py-1  ${
                defaultSpeaker.current === selectedPerson
                  ? 'bg-buttonDisabledBg text-base text-buttonDsiabledTxt pointer-events-none'
                  : 'hover:bg-[#0979b7]  text-white '
              }`}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
